import { CardType } from './card';

/**
 * "PriceHub 변경" 목록(GET /api/cards/hub-changes, 2026-10-21 — 매장별 판매가 3단계).
 *
 * - rise · drop: 로컬 수정가(PriceHub 매장 해석값) ≠ 기준가(네이버 현재 판매가) — "가격 상승"/"하락" 과 같은 집합
 * - hub · store: PriceHub 의 공통가 · 매장가 변경 중 위 둘에 안 잡힌 것
 * - notice:      매장 관리자가 네이버에서 직접 바꾼 값의 확정 대기 알림(확정 · 무시는 PriceHub 대시보드에서만 — CT 는 요청만)
 */
export type HubChangeKind = 'rise' | 'drop' | 'hub' | 'store' | 'notice';

/** 그 카드의 since 이후 마지막 PriceHub 변경 출처. hub=공통가 · store=매장가 · store_manager=매장 직접 변경. */
export type HubChangeSource = 'hub' | 'store' | 'store_manager';

export const HUB_CHANGE_KINDS: HubChangeKind[] = ['rise', 'drop', 'hub', 'store', 'notice'];

export interface HubChangeItem {
  /** 로컬 카드가 없는 PriceHub 항목이면 null(카드 칸 전부 null). */
  cardId:            number | null;
  sellerProductCode: string;
  productName:       string | null;
  cardType:          CardType | null;
  currentPrice:      number | null;
  hubPrice:          number | null;
  suggestedPrice:    number | null;
  priceChangeRate:   number | null;
  kind:              HubChangeKind;
  source:            HubChangeSource | null;
  /** 변경 시각(ISO 8601, 시간대 포함). */
  at:                string | null;
  /** since(기본 어제 07:30) 이후 바뀐 것인가 — "어제 이후만" 필터. */
  changedSince:      boolean;
  noticeId:          number | null;
  /** PriceHub 변경 · 알림의 이전 → 새 값(알림이면 previous → reported). */
  oldPrice:          number | null;
  newPrice:          number | null;
  appliesToStore:    boolean | null;
  hubSellingPrice:   number | null;
  game:              string | null;
  /** 매장 직접 변경 감지가 열려 있음 — PriceHub 관리자가 확정하기 전까지 네이버 반영에서 빠진다. */
  manualOverride:    boolean;
  manualOverrideAt:  string | null;
  /**
   * 카드당 한 줄 응답(백엔드 후속 — kind 는 notice 우선, 같은 카드의 다른 종류는 여기에)이 오면 쓴다.
   * 지금 응답에는 없을 수 있다.
   */
  flags?:            string[] | null;
  /**
   * 이 카드의 네이버 현재가를 PriceHub 매장가로 보낼 수 있음(POST /api/cards/hub-store-price/apply-current,
   * 백엔드 PR #44). false 면 현재가 없음(0원 · 미조회) · 상품코드 없음 · 매장가 대상이 아닌 게임 · 매장가 조회 꺼짐.
   * 예전 응답에는 없을 수 있다(없으면 false 로 본다).
   */
  canApplyCurrent?:  boolean;
  /**
   * 이 카드의 PriceHub 확정 대기 요청(백엔드 PR #47, 감사 card-controltower#71 — 요청 대기열). 없으면 null. 예전 응답에는
   * 칸이 없다(없으면 대기 없음으로 본다).
   */
  pendingRequest?:   HubPendingRequest | null;
  /** PriceHub 에서 이 카드의 판매가가 고정됨(③ 네이버 반영은 되지만 PriceHub 매장가 요청은 막힌다). 예전 응답엔 없다. */
  locked?:           boolean;
  /** canApplyCurrent=false 인 서버 사유(예: "요청 대기 중 …", "고정됨 …"). 있으면 프런트 문구 대신 그대로 보인다. */
  applyCurrentBlockReason?: string | null;
  /**
   * 확정 대기의 출처(백엔드 PR #73, 세 화면 재설계 B1) — decided(판매가 결정) · detected(네이버 직접 변경) · apply_current(매장 가격 요청) ·
   * manual_request · auto_rule(PriceHub 쪽) · null(대기 없음). 배지는 이 값으로(pendingRequest.kind 가 아니라). 예전 응답엔 없다.
   */
  noticeKind?: string | null;
}

/**
 * PriceHub 확정 대기 요청 종류 — apply_current(CT 현재가 요청) · detected(매장 직접 변경 감지) · auto_rule(규칙 제안) ·
 * manual_request. 모르는 값이 와도 표시만 하도록 string 으로 연다.
 */
export type HubPendingRequestKind = 'apply_current' | 'detected' | 'auto_rule' | 'manual_request' | string;

export interface HubPendingRequest {
  kind:        HubPendingRequestKind;
  /** 요청한 사람(SUPER 면 "<이름> (SUPER)"). 모르면 null. */
  requestedBy: string | null;
  /** 요청 시각(ISO 8601). */
  at:          string | null;
  /** 요청가. */
  price?:      number | null;
  /** PriceHub 알림 id(백엔드 PR #73). */
  noticeId?:   number | null;
}

export interface HubChangesResponse {
  since:             string;
  /** 이 목록의 PriceHub 매장 코드(백엔드 PR #44 — 예전 응답에는 없을 수 있다). */
  store?:            string | null;
  /** false 면 PriceHub 가 응답하지 않아 로컬 비교(rise · drop)만 들어 있다. */
  pricehubAvailable: boolean;
  pricehubTruncated: boolean;
  /** kind 별 건수(kind 필터 적용 전). */
  counts:            Partial<Record<HubChangeKind, number>>;
  items:             HubChangeItem[];
  /** PriceHub 확정 대기 요청이 있는 줄 수(카드당 한 번, kind 필터 전 — 탭 요약 "확정 대기 N건"). 예전 응답엔 없다. */
  pendingRequests?:  number;
}

/**
 * apply-current 결과 한 카드. 요청 대기열(백엔드 PR #47) 뒤로는 매장가를 바로 바꾸지 않고 PriceHub 에 **요청**한다 —
 * pending=true(notice "requested")면 PriceHub 관리자가 확정해야 반영되고 CT 수정가 · hub 값은 그대로다. notice
 * "already_same" 이면 PriceHub 가 이미 그 값이라 요청 없이 CT 값만 맞췄다(예전처럼 적용된 것으로 본다). pending 이
 * 없는 예전 응답은 지금 동작(바로 적용) 그대로.
 */
export interface ApplyCurrentApplied {
  cardId:   number;
  /** PriceHub 매장가로 보낸(요청한) 값(= 그 카드의 네이버 현재가). */
  hubPrice: number;
  notice:   'confirmed' | 'dismissed' | 'failed' | 'requested' | 'already_same' | null;
  /** 요청(또는 반영)은 됐지만 CT 쪽이 어긋난 사유. */
  warning:  string | null;
  /** PriceHub 확정 대기 요청으로 남았다(백엔드 PR #47). 예전 응답엔 없다. */
  pending?: boolean;
  /** 그 요청의 PriceHub 대기 id. */
  noticeId?: number | null;
}

/** 요청 대기열 응답의 "요청함"(확정 대기)인가 — 예전 응답(pending 없음)은 false. */
export const isPendingApplied = (a: ApplyCurrentApplied): boolean => a.pending === true;

/** POST /api/cards/hub-store-price/apply-current 응답(2026-10-21, 백엔드 PR #44). */
export interface ApplyCurrentResult {
  store:   string | null;
  applied: ApplyCurrentApplied[];
  failed:  { cardId: number; reason: string }[];
}
